import { useState, useEffect, useMemo } from "react";
import Step3ContactDetails from "./Step3ContactDetails";
import { Contact } from "@/types/api";
import { useContacts } from "@/hooks/api/useContacts";
import { Button } from "@/components/ui/button";

interface Step3ContactManagerProps {
  onBack: () => void;
  onComplete: () => void;
  isLoading?: boolean;
}

const Step3ContactManager = ({ onBack, onComplete, isLoading = false }: Step3ContactManagerProps) => {
  const [currentContactIndex, setCurrentContactIndex] = useState(0);
  const [processedContacts, setProcessedContacts] = useState<Set<number>>(new Set());
  const [initialContacts, setInitialContacts] = useState<Contact[]>([]);
  
  // Fetch contacts only once when component mounts
  const { data: contactsData, isLoading: isLoadingContacts } = useContacts({
    page: 1,
    page_size: 100, // Get all contacts, we'll filter client-side
  }, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Set initial contacts once when data loads
  useEffect(() => {
    if (contactsData?.results && initialContacts.length === 0) {
      const contactsToProcess = contactsData.results.filter(
        contact => !contact.relationship_type || contact.relationship_type === ""
      );
      setInitialContacts(contactsToProcess);
    }
  }, [contactsData?.results, initialContacts.length]);

  const currentContact = initialContacts[currentContactIndex];
  const isLastContact = currentContactIndex === initialContacts.length - 1;

  const handleContinue = () => {
    if (currentContact) {
      setProcessedContacts(prev => new Set(prev).add(currentContact.id));
    }

    if (isLastContact) {
      onComplete();
    } else {
      setCurrentContactIndex(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    handleContinue();
  };


  // If no contacts need details, show completion screen
  if (!isLoadingContacts && initialContacts.length === 0 && contactsData?.results) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 bg-white">
        <div className="w-full max-w-[621px] shadow-md p-8 md:p-[56px] rounded-md mb-16">
          <div className="mb-8 text-center">
            <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
              All set!
            </h1>
            <p className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
              You don't have any contacts that need additional details.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={onComplete}
              disabled={isLoading}
              className="w-full bg-[#6C4F77] hover:bg-[#5A3A68] text-white font-bold text-[14px] font-modernist py-[20px]"
            >
              {isLoading ? "Completing..." : "Complete Setup"}
            </Button>
            
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingContacts) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // If we have a current contact, show the details form
  if (currentContact) {
    return (
      <Step3ContactDetails
        contact={currentContact}
        onContinue={handleContinue}
        onSkip={handleSkip}
        isLastContact={isLastContact}
      />
    );
  }

  // If we've processed all contacts, complete the onboarding
  if (initialContacts.length > 0 && currentContactIndex >= initialContacts.length) {
    onComplete();
    return null;
  }

  // Fallback (shouldn't reach here)
  return null;
};

export default Step3ContactManager;